import React, { useCallback, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Paper,
  TableCell,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import RefreshIcon from '@mui/icons-material/Refresh';
import { daysUntil } from './orgFormat';
import { CONFIRM_CODE_HEADER, LicApiError } from './licApi';
import { colors } from '../../styles/tokens';
import { ConfirmDialog, LoadingState, PageHeader, SectionCard, StatusChip as BaseStatusChip } from '../../ui';

export { ConfirmDialog };

export const ACCENT = colors.gold;
export const HEADING = colors.ink;
export const MUTED = colors.muted;
export const BEIGE = colors.beige;

// Tables sit in a bordered card and scroll horizontally inside it on narrow screens.
export const tableContainerSx = {
  width: '100%',
  overflowX: 'auto',
  border: `1px solid ${colors.border}`,
  borderRadius: '14px',
  backgroundColor: colors.surface,
  mb: 3,
};

export const ViewHeader = ({ title, onRefresh, refreshing, children }) => (
  <PageHeader
    title={title}
    actions={
      (children || onRefresh) && (
        <>
          {children}
          {onRefresh && (
            <Tooltip title="Refresh">
              <span>
                <IconButton onClick={onRefresh} disabled={refreshing} aria-label="Refresh">
                  {refreshing ? <CircularProgress size={20} /> : <RefreshIcon />}
                </IconButton>
              </span>
            </Tooltip>
          )}
        </>
      )
    }
  />
);

export const Section = ({ title, children }) => <SectionCard title={title}>{children}</SectionCard>;

export const LoadingBox = () => <LoadingState />;

export const EmptyRow = ({ colSpan, children }) => (
  <TableRow>
    <TableCell colSpan={colSpan} align="center" sx={{ py: 4, color: MUTED }}>
      {children}
    </TableCell>
  </TableRow>
);

export const StatTile = ({ label, value, color = HEADING }) => (
  <Paper variant="outlined" sx={{ p: 2, flex: '1 1 140px', minWidth: 140 }}>
    <Typography variant="body2" sx={{ color: MUTED, fontWeight: 500 }}>
      {label}
    </Typography>
    <Typography variant="h4" component="p" sx={{ fontWeight: 700, color }}>
      {value}
    </Typography>
  </Paper>
);

/** Active / Suspended from the API, plus "Expired" when the expiry date has passed. */
export const licenseState = (org) => {
  if (!org) {
    return '';
  }
  if (org.status && org.status !== 'Active') {
    return org.status;
  }
  const days = daysUntil(org.expiresAtUtc);
  if (days !== null && days < 0) {
    return 'Expired';
  }
  return org.status || 'Active';
};

const STATE_TONES = { Active: 'success', Suspended: 'error', Expired: 'warning', Blocked: 'error' };

export const StatusChip = ({ org, status }) => {
  const label = status || licenseState(org);
  return <BaseStatusChip label={label} tone={STATE_TONES[label] || 'neutral'} />;
};

export const CopyButton = ({ value, label = 'Copy', onCopied }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      if (onCopied) {
        onCopied();
      }
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      setCopied(false);
    }
  };

  return (
    <Tooltip title={copied ? 'Copied' : label}>
      <span>
        <IconButton size="small" onClick={handleCopy} disabled={!value} aria-label={label}>
          <ContentCopyIcon fontSize="small" />
        </IconButton>
      </span>
    </Tooltip>
  );
};

/**
 * Runs dashboard actions that may need an emailed confirmation code. When an IT admin acts on another IT admin, the
 * API emails the acting admin a code and answers 428 ConfirmationRequired; the dialog asks for that code and retries.
 * `run(call)` takes `call(headers)` (the request) and resolves with its result, or rejects — with code 'Cancelled'
 * when the dialog is closed. Render `codeDialog` once in the screen.
 */
export const useCodeConfirmation = () => {
  const [pending, setPending] = useState(null); // { message, call, resolve, reject }
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [busy, setBusy] = useState(false);

  const run = useCallback(
    (call) =>
      call({}).catch((firstError) => {
        if (firstError?.code !== 'ConfirmationRequired') {
          throw firstError;
        }
        return new Promise((resolve, reject) => {
          setCode('');
          setError('');
          setInfo('');
          setPending({ message: firstError.message, call, resolve, reject });
        });
      }),
    []
  );

  const finish = (settle, value) => {
    settle(value);
    setPending(null);
  };

  const close = () => {
    if (pending) {
      finish(pending.reject, new LicApiError('Cancelled', 0, { code: 'Cancelled' }));
    }
  };

  const submit = async (event) => {
    event.preventDefault();
    if (!pending || busy) return;
    setBusy(true);
    setError('');
    setInfo('');
    try {
      finish(pending.resolve, await pending.call({ [CONFIRM_CODE_HEADER]: code.trim() }));
    } catch (submitError) {
      if (submitError?.code === 'InvalidCode') {
        setError(submitError.message);
      } else {
        finish(pending.reject, submitError);
      }
    } finally {
      setBusy(false);
    }
  };

  // Calling again without a code sends a new one (or just succeeds if a code is no longer needed).
  const resend = async () => {
    if (!pending || busy) return;
    setBusy(true);
    setError('');
    setInfo('');
    try {
      finish(pending.resolve, await pending.call({}));
    } catch (resendError) {
      if (resendError?.code === 'ConfirmationRequired') {
        setCode('');
        setInfo('A new code is on its way.');
      } else if (resendError?.code === 'TooManyCodes') {
        setError(resendError.message);
      } else {
        finish(pending.reject, resendError);
      }
    } finally {
      setBusy(false);
    }
  };

  const codeDialog = (
    <Dialog open={Boolean(pending)} onClose={busy ? undefined : close} fullWidth maxWidth="xs">
      <Box component="form" onSubmit={submit}>
        <DialogTitle>Confirm with a code</DialogTitle>
        <DialogContent>
          <DialogContentText>{pending?.message}</DialogContentText>
          <TextField
            autoFocus
            fullWidth
            size="small"
            sx={{ mt: 2 }}
            label="6-digit code"
            value={code}
            onChange={(event) => setCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
            error={Boolean(error)}
            helperText={error || info || ' '}
            inputProps={{ inputMode: 'numeric', autoComplete: 'one-time-code' }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={resend} disabled={busy} sx={{ mr: 'auto' }}>
            Send a new code
          </Button>
          <Button variant="outlined" onClick={close} disabled={busy}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" color="error" disabled={busy || code.length !== 6}>
            {busy ? <CircularProgress size={20} color="inherit" /> : 'Confirm'}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );

  return { run, codeDialog };
};
